import React, { useState, useEffect } from 'react';
import {
    Typography,
    Button,
    Form,
    Input,
    Card,
    App,
    Alert,
    Layout,
    Space
} from 'antd';
import {
    LockOutlined,
    ReloadOutlined
} from '@ant-design/icons';
import AppHeader from '../components/AppHeader/AppHeader';
import { usePageTitle } from '../context/PageTitleContext';
import { authService } from '../services/api';
import { useNavigate } from 'react-router-dom';

const { Title, Text } = Typography;
const { Password } = Input;
const { Content } = Layout;

const TemporaryPasswordPage = () => {
    const { setPageTitle, setRightContent } = usePageTitle();
    const { message } = App.useApp();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [form] = Form.useForm();

    // Сгенерированный пароль
    const [generatedPassword, setGeneratedPassword] = useState('');
    useEffect(() => {
        setPageTitle('Изменение временного пароля');

        return () => {
            setPageTitle('');
            setRightContent(null);
        };
    }, [setPageTitle, setRightContent]);

    useEffect(() => {
        // Проверяем, требуется ли смена временного пароля
        if (!authService.isAuthenticated()) {
            navigate('/login');
            return;
        }

        if (!authService.hasTemporaryPassword()) {
            navigate('/');
        }
    }, [navigate]);

    // Функция генерации пароля
    const generatePassword = () => {
        // Генерация пароля в стиле macOS Password app
        const uppercaseChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
        const lowercaseChars = 'abcdefghijklmnopqrstuvwxyz';
        const numberChars = '0123456789';
        const specialChars = '-._@+?!#$%^&*()';

        const allChars = uppercaseChars + lowercaseChars + numberChars + specialChars;

        // Минимум 1 символ из каждой категории
        let newPassword =
            uppercaseChars.charAt(Math.floor(Math.random() * uppercaseChars.length)) +
            lowercaseChars.charAt(Math.floor(Math.random() * lowercaseChars.length)) +
            numberChars.charAt(Math.floor(Math.random() * numberChars.length)) +
            specialChars.charAt(Math.floor(Math.random() * specialChars.length));

        // Добавляем случайные символы до нужной длины (14 символов)
        const length = 14;
        for (let i = newPassword.length; i < length; i++) {
            newPassword += allChars.charAt(Math.floor(Math.random() * allChars.length));
        }

        // Перемешиваем пароль
        newPassword = newPassword.split('').sort(() => 0.5 - Math.random()).join('');

        // Обновляем состояние для пароля
        setGeneratedPassword(newPassword);

        // Устанавливаем значения полей формы
        const formValues = {
            new_password: newPassword,
            confirm_password: newPassword
        };

        // Принудительно сбрасываем форму и устанавливаем новые значения
        setTimeout(() => {
            form.resetFields(['new_password', 'confirm_password']);
            form.setFieldsValue(formValues);
        }, 0);

        // Сообщение о генерации пароля
        message.success('Пароль успешно сгенерирован');
    };

    // Обработчик изменения пароля
    const handlePasswordChange = async (values) => {
        try {
            setLoading(true);
            const { temporary_password, new_password } = values;

            const response = await authService.changeTemporaryPassword(temporary_password, new_password);

            if (response.success) {
                message.success(response.message);
                // После успешной смены временного пароля проверяем наличие OAuth параметров
                const oauthParams = localStorage.getItem('oauth_params');
                if (oauthParams) {
                    // Перенаправляем на страницу OAuth авторизации с сохраненными параметрами
                    setTimeout(() => {
                        navigate('/oauth/authorize', {
                            state: {
                                oauthParams: JSON.parse(oauthParams)
                            }
                        });
                    }, 1500);
                } else {
                    // Стандартный редирект на главную
                    setTimeout(() => {
                        navigate('/');
                    }, 1500);
                }
            } else {
                message.error(response.message);
            }
        } catch (error) {
            message.error('Произошла ошибка при изменении пароля');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Layout style={{ minHeight: '100vh' }}>
            <AppHeader />
            <Content style={{ padding: '50px 20px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Card style={{ width: '100%', maxWidth: 500 }}>

                    <Alert
                        message="Требуется изменение пароля"
                        description="Вам необходимо изменить временный пароль, прежде чем продолжить работу в системе."
                        type="warning"
                        showIcon
                        style={{ marginBottom: 24 }}
                    />

                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={handlePasswordChange}
                    >
                        <Form.Item
                            name="temporary_password"
                            label="Временный пароль"
                            rules={[
                                { required: true, message: 'Пожалуйста, введите временный пароль' }
                            ]}
                        >
                            <Password placeholder="Введите временный пароль" />
                        </Form.Item>

                        <Form.Item
                            name="new_password"
                            label="Новый пароль"
                            rules={[
                                { required: true, message: 'Пожалуйста, введите новый пароль' },
                                { min: 8, message: 'Пароль должен содержать не менее 8 символов' }
                            ]}
                            extra="Нажмите на кнопку справа, чтобы сгенерировать надежный пароль"
                        >
                            <Space.Compact style={{ width: '100%' }}>
                                <Password
                                    prefix={<LockOutlined />}
                                    placeholder="Введите новый пароль"
                                />
                                <Button
                                    icon={<ReloadOutlined />}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        generatePassword();
                                    }}
                                    type="primary"
                                />
                            </Space.Compact>
                        </Form.Item>

                        <Form.Item
                            name="confirm_password"
                            label="Подтверждение пароля"
                            dependencies={['new_password']}
                            rules={[
                                { required: true, message: 'Пожалуйста, подтвердите пароль' },
                                ({ getFieldValue }) => ({
                                    validator(_, value) {
                                        if (!value || getFieldValue('new_password') === value) {
                                            return Promise.resolve();
                                        }
                                        return Promise.reject(new Error('Пароли не совпадают'));
                                    },
                                }),
                            ]}
                        >
                            <Password placeholder="Подтвердите новый пароль" />
                        </Form.Item>

                        <Form.Item style={{ marginBottom: 0, textAlign: 'center' }}>
                            <Button
                                type="primary"
                                htmlType="submit"
                                loading={loading}
                                size="large"
                                style={{ width: 200 }}
                            >
                                Изменить пароль
                            </Button>
                        </Form.Item>
                    </Form>
                </Card>
            </Content>
        </Layout>
    );
};

export default TemporaryPasswordPage;